/**
 * The slop tally (docs/notes.md "The slop tally", flag slopTallyUI; owner, 2026-10-23: "The board
 * should have a “days without Omie’s slop” tally in tally style. Anyone can reset it but there is
 * confirmation to do so"). A workplace "days without an accident" sign pinned across the top of the
 * notice board: the whole days since Omie (the household's dog) last left a mess, counted in the
 * household's own calendar, drawn as ink tally marks alone; a press and hold on the sign shows the
 * count as a number for as long as the finger stays down (owner, 2026-10-26: "Drop the plate, press
 * and hold will show the digit count and it will disappear when let go"). Anyone in the household
 * resets it, after a yes.
 *
 * Pure rules and copy, read by the server and both clients: the day count in a zone, the marks'
 * grouping and pixels, the best run, and every line the sign and its confirm say.
 */

/** What the board carries (BoardSummary.slop): when the count last started, who reset it, the best run, and the household's zone. */
export interface SlopTally {
  /** When the count last started, ISO UTC: the last reset, or the first time the household's board read it. */
  reset_at: string;
  /** Who reset it last; null before anyone has. */
  reset_by: string | null;
  /** The longest run before the current one, in whole days. */
  best_days: number;
  /** The household's zone (server/crown.ts householdZone): whose midnight a day turns at. */
  tz: string;
}

/** POST /api/slop/reset answers the sign as it stands after the reset. */
export type SlopResetResult = SlopTally;

// ---------- days ----------

const DAY_FORMATTERS = new Map<string, Intl.DateTimeFormat>();

function dayFormatter(tz: string): Intl.DateTimeFormat {
  let f = DAY_FORMATTERS.get(tz);
  if (!f) {
    try {
      f = new Intl.DateTimeFormat("en-US", { timeZone: tz, year: "numeric", month: "numeric", day: "numeric" });
    } catch {
      f = new Intl.DateTimeFormat("en-US", { timeZone: "UTC", year: "numeric", month: "numeric", day: "numeric" });
    }
    DAY_FORMATTERS.set(tz, f);
  }
  return f;
}

/** The calendar day an instant falls on in `tz`, as a day number (days since 1970-01-01 of that calendar). */
export function localDayNumber(at: number, tz: string): number {
  const parts = dayFormatter(tz).formatToParts(new Date(at));
  const n = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? NaN);
  return Math.floor(Date.UTC(n("year"), n("month") - 1, n("day")) / 86_400_000);
}

/**
 * Whole calendar days since the count started, in the household's zone: reset today is 0, yesterday
 * 1, whatever the hour. Never below 0 (a clock behind the server's), and 0 for a time that does not parse.
 */
export function slopDays(resetAt: string, now: number, tz: string): number {
  const at = Date.parse(resetAt);
  if (!Number.isFinite(at) || !Number.isFinite(now)) return 0;
  return Math.max(0, localDayNumber(now, tz) - localDayNumber(at, tz));
}

/** The best run the sign names: the longest finished run, or the current one once it is longer. */
export function slopBest(bestDays: number, days: number): number {
  return Math.max(Number.isFinite(bestDays) ? Math.max(0, Math.floor(bestDays)) : 0, days);
}

/** What a reset stores as the best: the current run counts once it is over. */
export function bestAfterReset(bestDays: number, days: number): number {
  return slopBest(bestDays, days);
}

// ---------- the marks ----------

/**
 * The most marks the sign draws: six groups of five, one row on a phone. Past it a small "+34" after
 * the marks says there is more, so a long run stays one tidy row; the hold shows the whole count.
 */
export const SLOP_TALLY_CAP = 30;

/** The marks as groups of five (the fifth slashed across the four), the last one partial: 7 is [5, 2]; 0 is []. */
export function tallyGroups(days: number, cap = SLOP_TALLY_CAP): number[] {
  const shown = Math.max(0, Math.min(Math.floor(days), cap));
  const out: number[] = [];
  for (let left = shown; left > 0; left -= 5) out.push(Math.min(5, left));
  return out;
}

/** The days past the marks ("+34" at 64), or 0 when every day has its mark. */
export function tallyOverflow(days: number, cap = SLOP_TALLY_CAP): number {
  return Math.max(0, Math.floor(days) - cap);
}

/** One group's art: 13 by 10 art pixels, drawn at 2 on both clients (crisp rects, like the stopwatch). */
export const TALLY_GROUP = { width: 13, height: 10, scale: 2, gap: 3 } as const;

/**
 * The four strokes, scratched by hand: each its column, its first and last row, and the rows at its top
 * that lean a pixel right (a pen's flick), so no two strokes are quite the same.
 */
const STROKES: readonly { x: number; top: number; bottom: number; lean: number }[] = [
  { x: 1, top: 1, bottom: 9, lean: 2 },
  { x: 4, top: 0, bottom: 8, lean: 0 },
  { x: 7, top: 1, bottom: 9, lean: 3 },
  { x: 10, top: 0, bottom: 9, lean: 0 },
];

/** The fifth mark: a line across the four, low on the left and high on the right. */
const SLASH = { x0: 0, y0: 7, x1: 12, y1: 2 } as const;

export interface TallyRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** A pixel line from one point to another (Bresenham), one art pixel thick. */
function line(x0: number, y0: number, x1: number, y1: number): TallyRect[] {
  const out: TallyRect[] = [];
  const dx = Math.abs(x1 - x0);
  const dy = -Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  let x = x0;
  let y = y0;
  for (;;) {
    out.push({ x, y, w: 1, h: 1 });
    if (x === x1 && y === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) {
      err += dy;
      x += sx;
    }
    if (e2 <= dx) {
      err += dx;
      y += sy;
    }
  }
  return out;
}

/** The rects of a group of `marks` (1 to 5) in art pixels: the strokes in order, the slash once there are five. */
export function tallyGroupRects(marks: number): TallyRect[] {
  const n = Math.max(0, Math.min(5, Math.floor(marks)));
  const out: TallyRect[] = [];
  STROKES.slice(0, Math.min(n, 4)).forEach((s) => {
    if (s.lean > 0) out.push({ x: s.x + 1, y: s.top, w: 1, h: s.lean });
    out.push({ x: s.x, y: s.top + s.lean, w: 1, h: s.bottom - s.top - s.lean + 1 });
  });
  if (n === 5) out.push(...line(SLASH.x0, SLASH.y0, SLASH.x1, SLASH.y1));
  return out;
}

// ---------- the words ----------

/** The sign's heading. */
export const SLOP_HEAD = "Days without Omie's slop";

/** What the sign says at 0, where the marks would be. */
export const SLOP_FRESH = "Fresh start.";

/** What the sign shows in place of the marks while it is held: the whole count, "23 days" ("64 days" past the cap, "0 days" at a fresh start). */
export function slopHeldLabel(days: number): string {
  return daysWord(Number.isFinite(days) ? Math.max(0, Math.floor(days)) : 0);
}

/**
 * How long a finger rests on the sign before the count shows, and how far it may drift meanwhile
 * (owner, 2026-10-30: "I don't like how the number flashes briefly when scrolling on the days
 * without slop"). A scroll that starts on the paper moves past the drift or is taken by the list
 * well inside the wait, so it never shows the count; a tap is shorter than the wait too.
 */
export const SLOP_HOLD_MS = 250;
export const SLOP_HOLD_DRIFT = 10;

/** The ink link that asks for the reset, and the confirm's two answers. */
export const SLOP_RESET_LABEL = "Reset";
export const SLOP_KEEP_LABEL = "Keep it";

/** What a screen reader hears for the Reset link. */
export const SLOP_RESET_A11Y = "Reset the days without Omie's slop";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "1 day", "23 days". */
export function daysWord(n: number): string {
  return n === 1 ? "1 day" : `${n} days`;
}

/** "Oct 23": the day an instant falls on in the household's zone. */
export function slopDateLabel(at: string, tz: string): string {
  const t = Date.parse(at);
  if (!Number.isFinite(t)) return "";
  const parts = dayFormatter(tz).formatToParts(new Date(t));
  const m = Number(parts.find((p) => p.type === "month")?.value);
  const d = Number(parts.find((p) => p.type === "day")?.value);
  return `${MONTHS[m - 1] ?? ""} ${d}`;
}

/** "Best: 31 days", or null while there is no run to speak of yet. */
export function slopBestLine(best: number): string | null {
  return best > 0 ? `Best: ${daysWord(best)}` : null;
}

/**
 * Who reset it and when, as the parts the sign draws (the name is a name tag with mentionsUI on):
 * { before: "Reset by ", name: "Jeff", after: ", Oct 23" }; before anyone has reset it, "Since Oct 23".
 */
export function slopResetParts(tally: Pick<SlopTally, "reset_at" | "reset_by" | "tz">): { before: string; name: string | null; after: string } {
  const day = slopDateLabel(tally.reset_at, tally.tz);
  const by = tally.reset_by?.trim();
  if (by) return { before: "Reset by ", name: by, after: day ? `, ${day}` : "" };
  return { before: day ? `Since ${day}` : "", name: null, after: "" };
}

/** The reset line as plain words ("Reset by Jeff, Oct 23"). */
export function slopResetLine(tally: Pick<SlopTally, "reset_at" | "reset_by" | "tz">): string {
  const p = slopResetParts(tally);
  return `${p.before}${p.name ?? ""}${p.after}`;
}

/** The confirm: its question and a line naming the count now. */
export function slopConfirmTitle(): string {
  return "Back to 0 days?";
}
export function slopConfirmLine(days: number): string {
  return days > 0 ? `It has been ${daysWord(days)} without Omie's slop.` : "It is already day 0.";
}

/** The confirm as one message, for a dialog that takes only one (the web's own). */
export function slopConfirmMessage(days: number): string {
  return `${slopConfirmTitle()}\n\n${slopConfirmLine(days)}`;
}

/** What a screen reader hears for the sign: "Days without Omie's slop: 23. Best: 31 days. Reset by Jeff, Oct 23." */
export function slopSignLabel(days: number, best: number, tally: Pick<SlopTally, "reset_at" | "reset_by" | "tz">): string {
  const parts = [`${SLOP_HEAD}: ${days}.`];
  const b = slopBestLine(best);
  if (b) parts.push(`${b}.`);
  const r = slopResetLine(tally);
  if (r) parts.push(`${r}.`);
  return parts.join(" ");
}

/** The refusal for a reset with no peon name on the session. */
export const SLOP_ANONYMOUS = "Pick your peon name first.";
